import { faIdBadge } from '@fortawesome/free-regular-svg-icons';
import { faBuilding, faRightFromBracket, faServer } from '@fortawesome/free-solid-svg-icons';
import { logoutUser } from 'api/auth';
import { companyKeys } from 'api/queryKeys';
import { ReactComponent as LogoIcon } from 'assets/icons/logo.svg';
import { useQueryClient } from '@tanstack/react-query';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useMeQuery } from 'hooks/queries/useMeQuery';
import { ReactComponent as MenuIcon } from 'assets/icons/menu.svg';
import { Avatar, Box, Dropdown, Flex, Menu } from 'components';
import { CompanySelectModal } from 'components/CompanySelectModal/CompanySelectModal';
import { Notifications } from 'components/Notifications';
import { P } from 'components/Typography/Typography';
import { navItems } from 'constants/navItems';
import { useAccount } from 'hooks/useAccount';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { device } from 'styles/theme';
import { IUser } from 'types/user';
import { ICompany } from 'types/company';

export interface INavbar {
  logged: boolean;
  onMenuIconClick: () => void;
  isSidebarOpen: boolean;
  hideMainNav?: boolean;
}

const Wrapper = styled(Flex)`
  border-bottom: 0.5px solid #e9e9e9;
  grid-area: navbar;
  width: 100%;
  height: 80px;

  ${({ theme }) => `
    background-color: ${theme.palette.neutral.white};
  `}
`;
const MenuToggleIcon = styled(MenuIcon)`
  display: none;
  margin-right: auto;
  padding: 10px;
  height: 50px;
  width: 50px;
  cursor: pointer;

  @media ${device.tablet} {
    display: block;
  }
`;

const TabletBox = styled(Box)`
  display: block;

  @media ${device.tablet} {
    display: none;
  }
`;

const Languages = styled(Flex)`
  align-items: center;

  span {
    &[data-val='true'] {
      border-bottom: 1px solid black;
    }
    cursor: pointer;
    margin: 0 10px;
  }
`;

const StyledLanguage = styled.button`
  background-color: transparent;
  padding: 5px;
  ${({ theme }) => `
    :focus-visible {
      outline: 1px solid ${theme.palette.neutral.grey};
    }
  `}
`;

const UserInfo = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
`;

const CompanyName = styled(P)`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.palette.neutral.grey};
`;

export const Navbar: React.FC<INavbar> = ({
  logged,
  onMenuIconClick,
  isSidebarOpen,
  hideMainNav = false
}) => {
  const { t, i18n } = useTranslation();
  const handleLangChange = (lang: string) => {
    localStorage.setItem('lang', lang);
    i18n.changeLanguage(lang);
  };
  const { loggedUserData, canManageCentralCompanies, canSwitchCompanyContext } = useAccount();

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: currentCompany = null } = useMainCompanyQuery();
  const { data: userDataFromQuery } = useMeQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState<IUser>();
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);

  useEffect(() => {
    if (logged && userDataFromQuery) {
      setUserData(userDataFromQuery);
    }
  }, [logged, userDataFromQuery]);

  const logout = async () => {
    setIsLoading(true);
    try {
      await logoutUser();
      localStorage.clear();
      navigate('/login');
    } catch {
      setIsLoading(false);
    }
  };

  const isInOwnCompany =
    !currentCompany ||
    currentCompany.id == null ||
    String(currentCompany.id) === String(loggedUserData.companyId);
  const showProfile = !canSwitchCompanyContext || isInOwnCompany;

  const profileDropdownItems = [
    ...(showProfile
      ? [
          {
            label: t('nav.profile'),
            onClick: () => {
              navigate('/app/my-profile');
            },
            icon: faIdBadge
          }
        ]
      : []),
    // Zmiana kontekstu firmy — personel platformy (superadmin + konsultant), nie surowa rola superadmin
    ...(canSwitchCompanyContext
      ? [
          {
            label: t('nav.changeCompany'),
            onClick: () => {
              setIsCompanyModalOpen(true);
            },
            icon: faBuilding
          }
        ]
      : []),
    ...(canManageCentralCompanies
      ? [
          {
            label: t('nav.platformCentral'),
            onClick: () => {
              navigate('/app/platform');
            },
            icon: faServer
          }
        ]
      : []),
    {
      label: t('nav.logout'),
      onClick: logout,
      icon: faRightFromBracket
    }
  ];

  useEffect(() => {
    if (localStorage.getItem('lang')) return;
    handleLangChange('pl');
  }, []);

  const getDisplayName = () => {
    const firstName = userData?.profile?.first_name || loggedUserData.firstName;
    const lastName = userData?.profile?.last_name || loggedUserData.lastName;
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    
    return loggedUserData.email;
  };

  const displayName = getDisplayName();
  const companyName = currentCompany?.short_name || currentCompany?.full_name || null;

  return (
    <Wrapper px={2} alignItems="center" justifyContent="space-between">
      <TabletBox>
        <LogoIcon />
      </TabletBox>

      <MenuToggleIcon onClick={onMenuIconClick} />

      {!hideMainNav && (
        <TabletBox>
          <Menu
            view="vertical"
            onMenuIconClick={onMenuIconClick}
            items={navItems}
          />
        </TabletBox>
      )}

      <Flex mr={10} alignItems="center">
        <Box mr={5}>
          <Notifications />
        </Box>

        <Dropdown items={profileDropdownItems}>
          <Avatar
            border
            url={`${process.env.REACT_APP_API_URL}${userData?.profile?.photo}`}
          />
          {canSwitchCompanyContext && companyName ? (
            <UserInfo mx={2}>
              <P variant="body" style={{ margin: 0 }}>
                {displayName}
              </P>
              <CompanyName variant="body">{companyName}</CompanyName>
            </UserInfo>
          ) : (
            <P variant="body" mx={2}>
              {displayName}
            </P>
          )}
        </Dropdown>
      </Flex>

      {/* Modal wyboru firmy */}
      {isCompanyModalOpen && (
        <CompanySelectModal
          isOpen={isCompanyModalOpen}
          onCancelClick={() => setIsCompanyModalOpen(false)}
          onCompanySwitched={() => {
            queryClient.invalidateQueries({ queryKey: companyKeys.main() });
            window.location.reload();
          }}
        />
      )}
    </Wrapper>
  );
};
